import React, { useState } from "react";
import { toast } from "react-toastify";

// COMPONENTS
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogModalFadeComponent from "@/main/components/modal/ModalFadeComponent";
import Button from "@/main/components/shared/button/button";

// UTILS/SERVICES/MODELS
import { quoteApi } from "@/main/api";
import { CartState } from "@/main/store/cart";
import { getGuidsQuoteItemInCart, getGuidsQuoteSupplierItemInCart } from "@/main/store/cart/cartUtils";
import { RemoveQuoteItemRequest } from "@/main/models/request";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";

// STYLES & COMPONENTS LOCAL
import { RefreshQuoteByGetItemsType, TypeBoxCart } from "../types";
import * as S from "./styles";

type TypeModalConfirmCancel = TypeBoxCart | "unasweredBulkCancel" | "noSupplierAssignedBulkCancel";
interface IModalConfirmCancelPartProps {
	guidsQuoteItem: string[];
	openModalConfirm: boolean;
	type?: TypeModalConfirmCancel;
	setOpenModalConfirm: React.Dispatch<React.SetStateAction<boolean>>;
	callbackAfterRemovePart?: (hasLastPartInCart: boolean) => void;
	refreshQuoteByGetItems: RefreshQuoteByGetItemsType;
	guidQuoteSupplierItem?: string | null;
	guidQuote: string;
	dataCart: CartState;
}

function ModalConfirmCancelPart({
	guidsQuoteItem,
	type = "unaswered",
	setOpenModalConfirm,
	callbackAfterRemovePart,
	guidQuoteSupplierItem,
	guidQuote,
	refreshQuoteByGetItems,
	dataCart,
}: IModalConfirmCancelPartProps) {
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

	const guidsQuoteItemAllCart = getGuidsQuoteItemInCart(dataCart);
	const guidsQuoteSupplierItemInCartAssigned = getGuidsQuoteSupplierItemInCart(dataCart);
	const removeGuidsQuoteItem = guidsQuoteItemAllCart.filter((guid) => !guidsQuoteItem.includes(guid));
	const isOnlyOneGuidInCartAssigned =
		isNullUndefined(guidsQuoteSupplierItemInCartAssigned) ||
		(!isNullUndefined(guidsQuoteSupplierItemInCartAssigned) && guidsQuoteSupplierItemInCartAssigned.length === 1);

	const hasLastPartInCart = type === "assigned" ? isOnlyOneGuidInCartAssigned : removeGuidsQuoteItem.length === 0;

	async function onClickActionConfirm() {
		try {
			setIsLoadingSave(true);
			const request: RemoveQuoteItemRequest = {
				guidQuote: guidQuote,
				guidQuoteItems: guidsQuoteItem,
				guidQuoteSupplierItem: !isNullUndefined(guidQuoteSupplierItem) ? guidQuoteSupplierItem : null,
				actionType: !isNullUndefined(type) ? type : null,
			};

			const {
				data: { result },
			} = await quoteApi.removeQuoteItems(request);

			const { isCanceledQuote } = refreshQuoteByGetItems(result);

			if (!isCanceledQuote) toast.success(getTranslateByKey("information.partcancel"));
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoadingSave(false);
			setOpenModalConfirm(false);
			if (!isNullUndefined(callbackAfterRemovePart)) {
				callbackAfterRemovePart(hasLastPartInCart);
			}
		}
	}
	const renderMessageConfirmationCancel = () => {
		const messageLastPartObj: Record<TypeModalConfirmCancel, { title: string; obs: string }> = {
			unaswered: {
				title: getTranslateByKey("information.confirmRemovePartCancelQuotePermanently"),
				obs: "",
			},
			noSupplierAssigned: {
				title: getTranslateByKey("information.confirmRemovePartCancelQuotePermanently"),
				obs: "",
			},
			assigned: {
				title: getTranslateByKey("information.confirmRemovePartCancelQuotePermanently"),
				obs: getTranslateByKey("information.obsconfirmRemovePartCancelQuotePermanently"),
			},
			unasweredBulkCancel: {
				title: getTranslateByKey("information.confirmRemovePartCancelQuotePermanentlyBulk"),
				obs: "",
			},
			noSupplierAssignedBulkCancel: {
				title: getTranslateByKey("information.confirmRemovePartCancelQuotePermanentlyBulk"),
				obs: "",
			},
		};
		const messageCancelPartObj: Record<TypeModalConfirmCancel, { title: string; obs: string }> = {
			unaswered: {
				title: getTranslateByKey("information.removePartItemInCart"),
				obs: getTranslateByKey("information.obsRemovePartItemInCart"),
			},
			noSupplierAssigned: {
				title: getTranslateByKey("information.confirmRemovePartPermanently"),
				obs: getTranslateByKey("information.obsRemovePartItemInCart"),
			},
			assigned: {
				title: getTranslateByKey("information.confirmRemovePartPermanently"),
				obs: getTranslateByKey("information.obsConfirmRemovePartPermanently"),
			},
			unasweredBulkCancel: {
				title: getTranslateByKey("information.removePartItemInCartBulk"),
				obs: getTranslateByKey("information.obsRemovePartItemInCartBulk"),
			},
			noSupplierAssignedBulkCancel: {
				title: getTranslateByKey("information.removePartItemInCartBulk"),
				obs: getTranslateByKey("information.obsRemovePartItemInCartBulk"),
			},
		};

		if (hasLastPartInCart) {
			return (
				<S.AreaMessageConfirmation>
					<p>{messageLastPartObj[type].title}</p>
					<p>{messageLastPartObj[type].obs}</p>
				</S.AreaMessageConfirmation>
			);
		}

		return (
			<S.AreaMessageConfirmation>
				<p>{messageCancelPartObj[type].title}</p>
				<p>{messageCancelPartObj[type].obs}</p>
			</S.AreaMessageConfirmation>
		);
	};
	return (
		<S.ContainerModalConfirmation>
			<h6>{renderMessageConfirmationCancel()}</h6>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					gap: "1rem",
				}}
			>
				<SecondaryButton
					type="button"
					disabled={isLoadingSave}
					onClick={() => {
						setOpenModalConfirm(false);
					}}
					id="id-createOrder-button-no"
				>
					{getTranslateByKey("information.no")}
				</SecondaryButton>
				<Button
					id="id-createOrder-button-yes"
					type="button"
					isLoading={isLoadingSave}
					onClick={() => onClickActionConfirm()}
				>
					{getTranslateByKey("information.yes")}
				</Button>
			</div>
		</S.ContainerModalConfirmation>
	);
}

function WrapperModalConfirmCancelPart(props: IModalConfirmCancelPartProps) {
	return (
		<KogModalFadeComponent isInModal open={props.openModalConfirm} setOpen={props.setOpenModalConfirm}>
			{props.openModalConfirm ? <ModalConfirmCancelPart {...props} /> : <div />}
		</KogModalFadeComponent>
	);
}
export default WrapperModalConfirmCancelPart;
